/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent } from 'react';
import {
  Controller,
  FieldValues,
  UseFormReturn,
  UseFormSetValue,
  useFormState,
} from 'react-hook-form';
import CalendarIcon from '@untitled-ui/icons-react/build/esm/Calendar';

import { DatePicker, Input, Select } from 'antd';
import AddressField, { AddressMode } from 'components/Inputs/AddressField';
import AddressInput, { Address } from 'components/Inputs/AddressInput';
import FieldError from 'components/Inputs/FieldError';
import MobilePhoneInput from 'components/Inputs/MobilePhoneInput';
import { endOfToday, isAfter, isBefore } from 'date-fns';
import { Dayjs } from 'dayjs';
import { mobilePhoneRules, visibleMobilePhoneError } from 'utils';

const { Option } = Select;

// react-hook-form nests errors to match the field path, so individuals[0].mobilePhone
// sits at errors.individuals[0].mobilePhone rather than under the whole path as a
// single key. Looking it up flat always returned undefined, which left this form with
// no error styling and no messages.
const getFieldError = (errors: Record<string, any>, path: string) =>
  path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .reduce<any>((error, key) => error?.[key], errors);

export const disabledDate = (current: any) => {
  const minDate = new Date(1900, 0, 1);
  return (
    current && (isAfter(current.toDate(), endOfToday()) || isBefore(current.toDate(), minDate))
  );
};

export const formIndividualFields: { [key: string]: any }[] = [
  {
    name: 'firstName',
    label: 'First Name',
    component: Input,
    rules: { required: 'First Name is required' },
    className: 'min-w-[194px]',
  },
  {
    name: 'middleName',
    label: 'Middle Name',
    component: Input,
    className: 'min-w-[194px]',
  },
  {
    name: 'lastName',
    label: 'Last Name',
    component: Input,
    rules: { required: 'Last Name is required' },
    className: 'min-w-[194px]',
  },
  {
    name: 'dateOfBirth',
    label: 'Date of Birth',
    // component: DatePicker,
    placeholder: '',
    suffixIcon: (
      <div className="border-l border-gray-300 h-10 flex items-center mt-0.5">
        <CalendarIcon className="text-gray-700 h-5 w-5 ml-2.5" />
      </div>
    ),
    rules: { required: 'Field required' },
    // disabledDate: disabledDate,
    className: 'min-w-[194px]',
  },
  {
    name: 'email',
    label: 'Email',
    component: Input,
    rules: {
      required: 'Field required',
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Invalid email address',
      },
    },
    className: 'min-w-[194px]',
  },
  {
    name: 'mobilePhone',
    label: 'Mobile Phone',
    component: MobilePhoneInput,
    rules: mobilePhoneRules,
    className: 'w-[302px]',
  },
  {
    name: 'address',
    label: 'Address',
    component: AddressInput,
    maxLength: 50,
    className: 'min-w-[620px]',
  },
  {
    name: 'relationship',
    label: 'Relationship',
    component: Select,
    rules: { required: 'Relationship is required' },
    isSelect: true,
    className: 'min-w-[302px]',
    placeholder: <div className="text-gray-900 font-semibold">Select</div>,
  },
];

interface AddNewIndividualFormProps {
  control: UseFormReturn['control'];
  register?: UseFormReturn['register'];
  errors: Record<string, any>;
  relationshipOptions?: any;
  formIndex?: number;
  hidden?: boolean;
  setValue: UseFormSetValue<FieldValues>;
  defaultValues?: {
    [key: string]: any;
  };
  manualAddress?: boolean;
  fieldGroup: string;
}

export const AddNewIndividualForm = ({
  control,
  errors,
  relationshipOptions,
  formIndex = 0,
  hidden = false,
  defaultValues,
  manualAddress,
  setValue,
  fieldGroup,
}: AddNewIndividualFormProps) => {
  const { isSubmitted, touchedFields } = useFormState({ control });

  return (
    <div className="flex flex-wrap items-start w-full gap-5 bg-gray-100 p-[14px] rounded-lg mt-4">
      {formIndividualFields?.map((field) => {
        const isRequired = field.rules?.required;
        const fieldName = `${fieldGroup}[${formIndex}]`;
        const path = `${fieldName}.${field.name}`;
        const fieldError =
          field.name === 'mobilePhone'
            ? visibleMobilePhoneError(
                getFieldError(errors, path),
                !!getFieldError(touchedFields, path),
                isSubmitted
              )
            : getFieldError(errors, path);

        if (field.name === 'relationship' && hidden) {
          return null;
        }

        let defaultValue = defaultValues?.[field.name] ?? '';

        if (defaultValues && field.name === 'address') {
          const { streetName, suburb, state, country, postcode } = defaultValues;
          defaultValue = [streetName, suburb, state, country, postcode].filter((a) => a).join(' ');
        }

        return (
          <div key={field.name} className={field.name === 'address' ? 'w-full' : ''}>
            <label className="block mb-1 text-gray-600 font-semibold text-xs">
              {field.label} {isRequired && '*'}
            </label>
            <Controller
              name={`${fieldName}.${field.name}`}
              control={control}
              rules={field.rules}
              defaultValue={defaultValue}
              render={({ field: controllerField }) => {
                if (field.isSelect) {
                  return (
                    <Select
                      {...controllerField}
                      className={`h-10 ${field.className || ''} font-medium`}
                      onChange={(e: ChangeEvent<HTMLInputElement> | any) => {
                        controllerField.onChange(e || null);
                      }}
                    >
                      {relationshipOptions?.map((relationship: any) => (
                        <Option key={relationship.value} value={relationship.value}>
                          {relationship.label}
                        </Option>
                      ))}
                    </Select>
                  );
                }

                if (field.name === 'address') {
                  if (manualAddress) {
                    const formattedAddress = [
                      defaultValues?.streetName,
                      defaultValues?.suburb,
                      defaultValues?.state,
                      defaultValues?.country,
                      defaultValues?.postcode,
                    ]
                      .filter((addressComponent) => addressComponent)
                      .join(' ');

                    return (
                      <AddressField
                        defaultMode={AddressMode.Manual}
                        defaultValue={{
                          street: defaultValues?.streetName || '',
                          suburb: defaultValues?.suburb || '',
                          state: defaultValues?.state || '',
                          postcode: defaultValues?.postcode || '',
                          country: defaultValues?.country || '',
                          formattedAddress,
                        }}
                        setValue={setValue}
                        control={control}
                        name={fieldName}
                        className={`h-10 ${field.className || ''} font-medium ${
                          fieldError ? 'border border-red-500' : ''
                        }`}
                        onChange={(value: Address) => controllerField.onChange(value || '')}
                      />
                    );
                  } else {
                    return (
                      <AddressInput
                        control={control}
                        name={`${fieldName}.${field.name}`}
                        className={`h-10 ${field.className || ''} font-medium ${
                          fieldError ? 'border border-red-500' : ''
                        }`}
                        onChange={(value: Address) => controllerField.onChange(value || '')}
                      />
                    );
                  }
                }

                if (field.name === 'mobilePhone') {
                  return (
                    <MobilePhoneInput
                      {...controllerField}
                      className={field.className}
                      status={fieldError ? 'error' : ''}
                    />
                  );
                }

                if (field.name === 'dateOfBirth') {
                  return (
                    <DatePicker
                      {...controllerField}
                      placeholder={field.placeholder}
                      suffixIcon={field.suffixIcon}
                      className={`h-10 ${field.className || ''} font-medium`}
                      status={fieldError ? 'error' : ''}
                      disabledDate={disabledDate}
                      format="DD/MM/YYYY"
                      maxLength={field.maxLength}
                      onChange={(date: Dayjs) => {
                        controllerField.onChange(date);
                      }}
                    />
                  );
                }

                return (
                  <field.component
                    {...controllerField}
                    placeholder={field.placeholder}
                    suffixIcon={field.suffixIcon}
                    className={`h-10 ${field.className || ''} font-medium`}
                    status={fieldError ? 'error' : ''}
                    rules={field.rules}
                    maxLength={field.maxLength}
                    onChange={(e: ChangeEvent<HTMLInputElement> | any) => {
                      let value;

                      if (field.name === 'relationship') {
                        value = e;
                      } else {
                        value = e.target?.value || '';
                      }

                      controllerField.onChange(value);
                    }}
                  />
                );
              }}
            />
            <FieldError message={fieldError?.message} />
          </div>
        );
      })}
    </div>
  );
};
